import type { BoxType, CustomBackgroundTemplateBox } from '@lite-workspace/dto-fotoclass-templates';
import type { DrawableBackgroundData } from '../../interfaces/drawable-data.interface';
import { buildObjectPassthroughMeta, carriedBoxFields, mergeBoxPassthroughMeta, objectPassthroughParams } from '../passthrough';
import { buildDrawableEffects, mergeDrawableEffects } from './effects';
import { rectFromBox, rectToBox } from './rect';
import { mergeRotation } from './rotation';
import type { BoxDecoder, BoxEncoder } from './codec-contract';

export const decode: BoxDecoder<CustomBackgroundTemplateBox, DrawableBackgroundData> = (box, ctx) => {
  if (box.params.pixelData !== undefined) {
    ctx.drop('params.pixelData removed in Phase 3');
  }

  if (box.params.maskDataUrl !== undefined) {
    ctx.warn('legacy inline maskDataUrl has no persisted slot on DrawableBackgroundData');
  }

  return {
    id: box.id,
    type: 'background',
    ...carriedBoxFields(box),
    order: 0, // dispatcher проставляет реальный order по индексу — не забота кодека
    canResize: false,
    opacity: box.opacity,
    blendMode: box.blendMode,
    visible: box.visible,
    // The editor padlock rides with the presentation fields: dropping it here would re-lock every
    // background a user released the moment the v1 persist schema goes live.
    locked: box.locked,
    rotation: box.rotation,
    effects: buildDrawableEffects(box.effects),
    userApplied: box.params.userApplied,
    packApplied: box.params.packApplied,
    sourceSpreadId: box.params.sourceSpreadId,
    sourcePackId: box.params.sourcePackId,
    placed: box.params.placed,
    data: {
      url: box.params.url ?? '',
      fileId: box.params.fileId ?? '',
      versions: box.params.versions,
    },
    maskRect: box.params.maskRect,
    maskUrl: box.params.maskUrl,
    maskFileId: box.params.maskFileId,
    naturalAligns: {
      container: rectFromBox(box.rect, ctx),
      content: box.contentRect ? rectFromBox(box.contentRect, ctx.child('contentRect')) : undefined,
    },
    templateMeta: buildObjectPassthroughMeta(box, box.params),
  };
};

export const encode: BoxEncoder<DrawableBackgroundData, CustomBackgroundTemplateBox> = (drawable, ctx) => {
  const box: CustomBackgroundTemplateBox = {
    type: 'background' as BoxType.BACKGROUND,
    id: drawable.id,
    ...carriedBoxFields(drawable),
    rect: rectToBox(drawable.naturalAligns.container, ctx),
    contentRect: drawable.naturalAligns.content ? rectToBox(drawable.naturalAligns.content, ctx) : undefined,
    rotation: mergeRotation(drawable, ctx),
    opacity: drawable.opacity,
    blendMode: drawable.blendMode,
    visible: drawable.visible,
    locked: drawable.locked,
    effects: mergeDrawableEffects(
      drawable.effects,
      drawable.templateMeta?.rawEffects,
      drawable.layerStyleRendering === 'live' ? 'typed' : undefined,
    ),
    params: {
      url: drawable.data.url,
      fileId: drawable.data.fileId,
      versions: drawable.data.versions,
      maskRect: drawable.maskRect,
      maskUrl: drawable.maskUrl,
      maskFileId: drawable.maskFileId,
      userApplied: drawable.userApplied,
      packApplied: drawable.packApplied,
      sourceSpreadId: drawable.sourceSpreadId,
      sourcePackId: drawable.sourcePackId,
      placed: drawable.placed,
      ...objectPassthroughParams(drawable.templateMeta),
    },
  };

  return mergeBoxPassthroughMeta(box, drawable.templateMeta);
};
